import {
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native'
import {Feather} from '@expo/vector-icons'
import {useLingui} from '@lingui/react/macro'

import {SLIDE_ACCENT, SLIDE_FEATHER_ICON} from '#/lib/slideTokens'
import {type SlideEditorProps} from '#/components/capsules/play/types'
import {StudioTextField} from '#/components/studio/StudioTextField'
import {readableAccent, type StudioChrome} from '#/themes-studio/chrome'
import {useStudioChrome, useStudioStyles} from '#/themes-studio/useChrome'
import {SLIDE_TYPE_META} from '#/types/capsule'
import {type SlideOption} from '#/types/capsule'

function uid() {
  return Math.random().toString(36).slice(2)
}

export function SingleChoiceSlideEditor({
  slide,
  onChange,
  primaryColor: _primaryColor,
  onPreview,
}: SlideEditorProps) {
  const ss = useStyles()
  const chrome = useStudioChrome()
  const {t: l} = useLingui()
  const accent = SLIDE_ACCENT.single_choice
  // Ink, not fill: the taxonomy's colour tuned to read on the studio surface it
  // is written on (see `readableAccent`). Tints stay on the raw hue.
  const accentInk = readableAccent(accent, chrome)
  const meta = SLIDE_TYPE_META.single_choice
  const slideIcon = SLIDE_FEATHER_ICON.single_choice ?? 'help-circle'
  const options: SlideOption[] = slide.options ?? []

  const addOption = () => {
    onChange({options: [...options, {id: uid(), label: '', isCorrect: false}]})
  }

  const removeOption = (id: string) => {
    onChange({options: options.filter(o => o.id !== id)})
  }

  const setCorrect = (id: string) => {
    onChange({options: options.map(o => ({...o, isCorrect: o.id === id}))})
  }

  return (
    <ScrollView
      style={ss.root}
      contentContainerStyle={ss.inner}
      keyboardShouldPersistTaps="handled"
      showsVerticalScrollIndicator={false}>
      {/* Type badge row */}
      {/* Host-provided header (e.g. FlashBook studio) replaces this row */}
      <View style={[ss.topRow, onPreview ? null : {display: 'none'}]}>
        <View
          style={[
            ss.typeBadge,
            {
              backgroundColor: accent + '22',
              borderColor: accent + '44',
            },
          ]}>
          <Feather name={slideIcon} size={12} color={accentInk} />
          <Text style={[ss.typeBadgeText, {color: accentInk}]}>
            {meta?.label ?? slide.type}
          </Text>
        </View>
        <TouchableOpacity
          accessibilityRole="button"
          onPress={onPreview}
          hitSlop={6}
          style={[
            ss.previewBtn,
            {
              backgroundColor: accent + '18',
              borderColor: accent + '55',
            },
          ]}>
          <Feather name="play-circle" size={13} color={accentInk} />
          <Text
            style={[ss.previewBtnText, {color: accentInk}]}>{l`Preview`}</Text>
        </TouchableOpacity>
      </View>

      {/* Question */}
      <StudioTextField
        accessibilityLabel="Text input field"
        accessibilityHint={undefined}
        style={ss.question}
        value={slide.question ?? ''}
        onChangeText={v => onChange({question: v})}
        placeholder={l`Écris ta question ici…`}
        placeholderTextColor={chrome.textFaint}
      />

      {/* Options */}
      <Text style={ss.sectionLabel}>
        {l`OPTIONS (one correct answer)`.toUpperCase()}
      </Text>
      {options.map((opt, i) => (
        <View key={opt.id} style={ss.optRow}>
          <TouchableOpacity
            accessibilityRole="radio"
            onPress={() => setCorrect(opt.id)}
            style={ss.radioBtn}>
            <Feather
              name={opt.isCorrect ? 'check-circle' : 'circle'}
              size={22}
              color={opt.isCorrect ? accent : chrome.textFaint}
            />
          </TouchableOpacity>
          <View
            style={[
              ss.optCard,
              {
                backgroundColor: opt.isCorrect ? accent + '18' : chrome.fill,
                borderColor: opt.isCorrect
                  ? accent + '66'
                  : chrome.borderStrong,
              },
            ]}>
            <TextInput
              accessibilityLabel="Text input field"
              accessibilityHint={undefined}
              style={ss.optInput}
              value={opt.label}
              onChangeText={v =>
                onChange({
                  options: options.map(o =>
                    o.id === opt.id ? {...o, label: v} : o,
                  ),
                })
              }
              placeholder={`Option ${String.fromCharCode(65 + i)}`}
              placeholderTextColor={chrome.textFaint}
            />
          </View>
          {options.length > 2 && (
            <TouchableOpacity
              accessibilityRole="button"
              onPress={() => removeOption(opt.id)}>
              <Feather name="trash-2" size={15} color={chrome.textFaint} />
            </TouchableOpacity>
          )}
        </View>
      ))}
      {options.length < 6 && (
        <TouchableOpacity
          accessibilityRole="button"
          onPress={addOption}
          style={ss.addBtn}>
          <Feather name="plus" size={14} color={chrome.textFaint} />
          <Text style={ss.addTxt}>{l`Add an option`}</Text>
        </TouchableOpacity>
      )}

      {/* Explanation */}
      <View style={ss.explanationWrap}>
        <Text style={ss.explanationLabel}>
          {l`EXPLICATION (optionnelle)`.toUpperCase()}
        </Text>
        <View style={ss.explanationCard}>
          <StudioTextField
            accessibilityLabel="Text input field"
            accessibilityHint={undefined}
            style={ss.explanationInput}
            value={slide.explanation ?? ''}
            onChangeText={v => onChange({explanation: v})}
            placeholder={l`Explication affichée après la réponse…`}
            placeholderTextColor={chrome.textFaint}
            textAlignVertical="top"
          />
        </View>
      </View>
    </ScrollView>
  )
}

const makeStyles = (chrome: StudioChrome) =>
  StyleSheet.create({
    root: {flex: 1, backgroundColor: chrome.frame},
    inner: {padding: 20, paddingBottom: 40, gap: 16},
    topRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    typeBadge: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      alignSelf: 'flex-start',
      paddingHorizontal: 10,
      paddingVertical: 5,
      borderRadius: 20,
      borderWidth: 1,
    },
    typeBadgeText: {fontSize: 11, fontWeight: '700'},
    previewBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
      paddingHorizontal: 10,
      paddingVertical: 5,
      borderRadius: 20,
      borderWidth: 1,
    },
    previewBtnText: {fontSize: 11, fontWeight: '700'},
    question: {
      color: chrome.text,
      fontSize: 20,
      fontWeight: '700',
      lineHeight: 28,
      paddingVertical: 0,
    },
    sectionLabel: {
      color: chrome.textFaint,
      fontSize: 10,
      fontWeight: '700',
      letterSpacing: 0.9,
    },
    optRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
    },
    radioBtn: {
      width: 30,
      height: 30,
      alignItems: 'center',
      justifyContent: 'center',
    },
    optCard: {
      flex: 1,
      borderRadius: 14,
      borderWidth: 1.5,
      paddingHorizontal: 14,
      paddingVertical: 11,
    },
    optInput: {color: chrome.text, fontSize: 15, paddingVertical: 0},
    addBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      paddingVertical: 10,
      paddingHorizontal: 14,
      borderRadius: 14,
      borderWidth: 1.5,
      borderStyle: 'dashed',
      borderColor: chrome.borderStrong,
    },
    addTxt: {color: chrome.textFaint, fontSize: 13},
    explanationWrap: {gap: 6},
    explanationLabel: {
      color: chrome.textFaint,
      fontSize: 10,
      fontWeight: '700',
      letterSpacing: 0.9,
    },
    explanationCard: {
      backgroundColor: chrome.fill,
      borderRadius: 12,
      borderWidth: 1,
      borderColor: chrome.borderStrong,
      paddingHorizontal: 14,
      paddingVertical: 10,
    },
    explanationInput: {
      color: chrome.textMuted,
      fontSize: 13,
      lineHeight: 18,
      paddingVertical: 0,
    },
  })

/** The editor's styles for the appearance the app is in. */
const useStyles = () => useStudioStyles(makeStyles)
